import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { PageTitle, Panel, Btn, Sel, NameFields, emptyName, toNamePayload } from "@/admin/ui";

const csv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

export default function Golden() {
  const [tests, setTests] = useState([]);
  const [n, setN] = useState(emptyName);
  const [f, setF] = useState({ label: "", rules: "", cats: "", structs: "", notes: "" });
  const [ruleset, setRuleset] = useState("draft");
  const [run, setRun] = useState(null);
  const load = () => api.get("/admin/golden").then((r) => setTests(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    try {
      await api.post("/admin/golden", { label: f.label || n.first, input: toNamePayload(n), expected_rule_ids: csv(f.rules), expected_categories: csv(f.cats), expected_structures: csv(f.structs), notes: f.notes });
      toast.success("Golden test added"); setN(emptyName); setF({ label: "", rules: "", cats: "", structs: "", notes: "" }); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { await api.delete(`/admin/golden/${id}`); load(); };
  const runAll = async () => { try { setRun((await api.post("/admin/golden/run", { ruleset })).data); } catch (e) { toast.error(errMsg(e)); } };
  const result = (id) => run?.results.find((r) => r.id === id);

  return (
    <div className="space-y-6">
      <PageTitle title="Golden Tests" sub="Manually verified reference cases. Publishing a ruleset is blocked unless every golden test passes.">
        <Sel value={ruleset} onChange={setRuleset} options={[["draft", "Draft"], ["active", "Active"]]} testId="golden-ruleset-select" />
        <Btn onClick={runAll} data-testid="golden-run-btn">Run all tests</Btn>
      </PageTitle>
      {run && <p className="text-sm" data-testid="golden-run-summary">{run.passed} / {run.total} passed against {run.ruleset}</p>}
      <Panel title="Add test case" testId="golden-form">
        <NameFields n={n} setN={setN} prefix="golden" />
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Input placeholder="Label" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} data-testid="golden-label-input" />
          <Input placeholder="Expected rule IDs (comma)" value={f.rules} onChange={(e) => setF({ ...f, rules: e.target.value })} data-testid="golden-rules-input" />
          <Input placeholder="Expected categories (comma)" value={f.cats} onChange={(e) => setF({ ...f, cats: e.target.value })} />
          <Input placeholder="Expected structures (comma), e.g. first_is_initials" value={f.structs} onChange={(e) => setF({ ...f, structs: e.target.value })} data-testid="golden-structs-input" />
          <Input placeholder="Notes / approved reference findings" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="sm:col-span-2" />
        </div>
        <Btn className="mt-3" disabled={!n.first.trim()} onClick={add} data-testid="golden-add-btn">Add test</Btn>
      </Panel>
      <Panel title={`Test cases (${tests.length})`} testId="golden-list">
        <ul className="divide-y divide-border text-sm">
          {tests.map((t) => {
            const r = result(t.id);
            return (
              <li key={t.id} className="py-3" data-testid={`golden-row-${t.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span><strong>{t.label}</strong> <span className="text-muted-foreground">· {t.expected_rule_ids.length} rules · {t.expected_structures.join(", ")}</span></span>
                  <span className="flex items-center gap-3">
                    {r && <span className={r.passed ? "text-emerald-400" : "text-red-400"} data-testid={`golden-result-${t.id}`}>{r.passed ? "PASS" : "FAIL"}</span>}
                    <button onClick={() => del(t.id)} className="text-xs text-muted-foreground hover:text-red-400">Delete</button>
                  </span>
                </div>
                {r && !r.passed && <pre className="mt-2 overflow-x-auto rounded bg-secondary p-2 font-mono-ui text-xs">{r.error || JSON.stringify(r.diffs, null, 1)}</pre>}
              </li>
            );
          })}
          {!tests.length && <li className="py-3 text-muted-foreground">No golden tests yet — aim for at least 20 verified cases before launch.</li>}
        </ul>
      </Panel>
    </div>
  );
}
